class SearchModal extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-search-input]');
    this.resultsEl = this.querySelector('[data-search-results]');
    this.form = this.querySelector('[data-search-form]');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.predictiveUrl = this.dataset.predictiveSearchUrl;
    this.debounceTimer = null;
    this.currentController = null;

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-search-close]')) this.close();
      if (event.target.closest('[data-search-clear]')) this.#clear();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.hidden) this.close();
    });

    if (this.input) {
      this.input.addEventListener('input', () => this.#onInput());
    }

    if (this.form) {
      this.form.addEventListener('submit', (event) => {
        // Let it submit normally to routes.search_url — full-page search
        // already indexes title/tags/sku by default, no JS needed here.
        if (!this.input.value.trim()) event.preventDefault();
      });
    }
  }

  #onInput() {
    const query = this.input.value.trim();
    clearTimeout(this.debounceTimer);

    if (this.clearButton) this.clearButton.hidden = query.length === 0;

    if (query.length < 2) {
      this.resultsEl.innerHTML = '';
      return;
    }

    this.debounceTimer = setTimeout(() => this.#search(query), 300);
  }

  #clear() {
    this.input.value = '';
    this.resultsEl.innerHTML = '';
    if (this.clearButton) this.clearButton.hidden = true;
    this.input.focus();
  }

  async #search(query) {
    if (this.currentController) this.currentController.abort();
    this.currentController = new AbortController();

    this.resultsEl.setAttribute('aria-busy', 'true');

    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': '6',
      'resources[options][fields]': 'title,vendor,tag,variants.sku,variants.barcode',
      section_id: 'predictive-search-results',
    });

    const baseUrl = this.predictiveUrl.replace(/\.json$/, '');

    try {
      const response = await fetch(`${baseUrl}?${params.toString()}`, {
        signal: this.currentController.signal,
      });

      if (!response.ok) {
        const body = await response.text();
        console.error('[search-modal] predictive search request failed', response.status, body);
        this.resultsEl.innerHTML = '';
        return;
      }

      const raw = await response.text();
      let html = '';

      try {
        const data = JSON.parse(raw);
        html = data.sections ? data.sections['predictive-search-results'] : '';
        if (!html) {
          console.warn('[search-modal] no "predictive-search-results" key in response — check that sections/predictive-search-results.liquid exists and section_id matches.', data);
        }
      } catch (parseError) {
        // Fallback: some setups return the section's raw HTML directly
        // instead of the {sections: {...}} JSON wrapper.
        html = raw;
      }

      this.resultsEl.innerHTML = html || '';
      this.#fetchRealCount(query);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[search-modal] predictive search error', error);
        this.resultsEl.innerHTML = '';
      }
    } finally {
      this.resultsEl.removeAttribute('aria-busy');
    }
  }

  async #fetchRealCount(query) {
    const countEl = this.resultsEl.querySelector('[data-search-count]');
    if (!countEl || !this.form) return;

    const searchUrl = this.form.getAttribute('action');
    const params = new URLSearchParams({ q: query, type: 'product' });
    const fetchUrl = `${searchUrl}?${params.toString()}`;

    try {
      const response = await fetch(fetchUrl);
      const raw = await response.text();

      if (!response.ok) {
        console.error('[search-modal] real count fetch failed', response.status, raw);
        return;
      }

      const doc = new DOMParser().parseFromString(raw, 'text/html');
      const valueEl = doc.querySelector('[data-search-total-count]');

      if (!valueEl) {
        console.warn('[search-modal] no [data-search-total-count] found on the search page — check that sections/search.liquid was updated.', raw);
        return;
      }

      const count = parseInt(valueEl.textContent.trim(), 10);
      if (Number.isNaN(count)) return;

      // The element may have been replaced by a newer search in the meantime.
      const freshCountEl = this.resultsEl.querySelector('[data-search-count]');
      if (!freshCountEl) return;

      const label = count === 1 ? 'disco encontrado' : 'discos encontrados';
      freshCountEl.textContent = `${count} ${label}`;
    } catch (error) {
      console.error('[search-modal] real count fetch error', error);
    }
  }

  open() {
    this.hidden = false;
    requestAnimationFrame(() => this.classList.add('is-open'));
    document.documentElement.classList.add('search-modal-open');
    if (this.input) {
      this.input.value = '';
      this.resultsEl.innerHTML = '';
      if (this.clearButton) this.clearButton.hidden = true;
      setTimeout(() => this.input.focus(), 100);
    }
  }

  close() {
    this.classList.remove('is-open');
    document.documentElement.classList.remove('search-modal-open');
    const onEnd = () => {
      this.hidden = true;
      this.removeEventListener('transitionend', onEnd);
    };
    this.addEventListener('transitionend', onEnd);
  }
}

if (!customElements.get('search-modal')) {
  customElements.define('search-modal', SearchModal);
}

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-search-open]');
  if (!opener) return;
  const modal = document.querySelector('search-modal');
  if (modal) modal.open();
});
